<template>
    <div class="card rounded-16">
        <div class="card-header bg-grey p-2 d-flex align-items-center gap-2 rounded-top">
            <b class="text-16">Billing information</b>
        </div>
        <div class="card-body">
            <div class="row row-cols-1 row-cols-sm-auto">
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="forename">First name*</label>
                    <Popper :content="errors.forename" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.forename">
                        <Field id="forename" name="forename" type="text" placeholder="First Name" class="form-control mb-2" :class="{'error': errors.forename}"/>
                    </Popper>
                </div>
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="surname">Last name*</label>
                    <Popper :content="errors.surname" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.surname">
                        <Field id="surname" name="surname" type="text" placeholder="Last Name" class="form-control mb-2" :class="{'error': errors.surname}"/>
                    </Popper>
                </div>
            </div>
            <div class="row row-cols-1 row-cols-sm-auto">
                <div class="d-flex flex-column" v-if="customValidationMessage.ENABLED">
                    <Field name="email" v-slot="{ field, errorMessage, setTouched, validate }">
                        <label class="text-14 mb-1" for="email">Uber Rides email address*</label>
                        <Popper
                            :content="errorMessage"
                            arrow
                            placement="top"
                            class="w-100 m-0 border-0"
                            :show="!!errorMessage"
                        >
                            <input
                            id="email"
                            type="email"
                            placeholder="Uber Rides email address"
                            class="form-control mb-2"
                            :class="{ error: !!errorMessage }"
                            v-bind="field"
                            @focus="
                                if (!errorMessage) {
                                setTouched(true);
                                validate();  // show initial message immediately
                                }
                            "
                            @input.stop
                            />
                        </Popper>
                    </Field>
                </div>
                <div class="d-flex flex-column" v-else>
                    <label class="text-14 mb-1" for="email">Email address*</label>
                    <Popper :content="errors.email" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.email">
                        <Field id="email" name="email" type="email" placeholder="Email Address" class="form-control mb-2" :class="{'error': errors.email}"/>
                    </Popper>
                </div>
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="postcode">Postcode*</label>
                    <Popper :content="errors.postcode" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.postcode">
                        <Field id="postcode" name="postcode" type="text" placeholder="Postcode" @change="this.$parent.$parent.postcodeLookup($event.target.value, true)" :value="postcode" class="form-control mb-2" :class="{'error': errors.postcode}" />
                    </Popper>
                </div>
            </div>
            <div class="row row-cols-1 row-cols-sm-auto my-2 align-items-center">
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="address">Select address*</label>
                    <Popper :content="errors.address" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.address">
                        <Field id="address" v-slot="{ value }" name="address" as="select" class="form-select cursor-pointer" :class="{'error': errors.address}">
                            <option value="" disabled>Select an address</option>
                            <template v-for="address, key in addresses">
                                <template v-if="key != 0">
                                    <option :key="key" :value="key" :selected="value && value.includes(address)">{{ address }}</option>
                                </template>
                            </template>
                        </Field>
                    </Popper>
                </div>
                <div class="d-flex flex-column mt-2 mt-lg-3">
                    <label for="input_address_manually" class="d-inline-block cursor-pointer">
                        <span class="text-primary fw-bold" @click="this.$emit('inputAddressManually')">Enter address manually</span>
                        <Field type="checkbox" id="input_address_manually" name="input_address_manually" class="d-none" :value="true" v-model="inputAddressManually"/>
                    </label>
                </div>
            </div>
            <div class="row row-cols-1 row-cols-sm-auto my-2" v-if="inputAddressManually == true">
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="address1">Address 1</label>
                    <Popper :content="errors.address1" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.address1">
                        <Field id="address1" name="address1" type="text" placeholder="Address 1" class="form-control" :class="{'error': errors.address1}"/>
                    </Popper>
                </div>
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="address2">Address 2</label>
                    <Field id="address2" name="address2" type="text" placeholder="Address 2" class="form-control" />
                </div>
            </div>
            <div class="row row-cols-1 row-cols-sm-auto my-2" v-if="inputAddressManually == true">
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="address3">Address 3</label>
                    <Field id="address3" name="address3" type="text" placeholder="Address 3" class="form-control" />
                </div>
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="town">Town</label>
                    <Popper :content="errors.town" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.town">
                        <Field id="town" name="town" type="text" placeholder="Town" class="form-control" :class="{'error': errors.town}"/>
                    </Popper>
                </div>
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="county">County</label>
                    <Field id="county" name="county" type="text" placeholder="County" class="form-control" />
                </div>
            </div>
            <div class="row row-cols-1 row-cols-sm-auto">
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="telephone">Telephone*</label>
                    <Popper :content="errors.telephone" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.telephone">
                        <Field id="telephone" name="telephone" type="text" placeholder="Telephone Number" class="form-control mb-2" :class="{'error': errors.telephone}"/>
                    </Popper>
                </div>
                <div class="d-flex flex-column">
                    <label class="text-14 mb-1" for="registration">Registration*</label>
                    <Popper :content="errors.registration" arrow placement="top" class="w-100 m-0 border-0" :show="!!errors.registration">
                        <Field id="registration" name="registration" type="text" placeholder="Registration" v-model="updatedRegistration" class="form-control mb-2" :class="{'error': errors.registration}"/>
                    </Popper>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import { Field , ErrorMessage} from 'vee-validate';
    import bespoke from '@shared/mixins/bespoke';
    export default {
        props: ['basket', 'registration', 'postcode', 'addresses', 'errors', 'customValidationMessage'],
        components: { Field, ErrorMessage },
        mixins: [bespoke],
        data() {
            return {
                inputAddressManually: false,
                updatedRegistration: this.registration
            }
        },
        mounted() {
        },
        methods: {

        }
    }
</script>
<style lang="scss" scoped>
    .form-select {
        background-position-x: 97%;
        @media screen and (min-width: 576px) and (max-width: 767px) {
            width: 177px;
        }
    }
    .row-cols-sm-auto {
        .form-control, .form-select {
            @media screen and (min-width: 768px) {
              width: 280px;
            }
            //@media screen and (max-width: 575px) {
            //    width: 100%;
            //}
        }
    }
</style>